"use client";

import { useEffect, useRef, useState } from "react";

import { Check, Info, Plus, Upload, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import Image from "next/image";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { toast } from "react-toastify";

import { TextEffect } from "@/components/motion-primitives/core/text-effect";

import { WorkspaceDetailSerializer } from "@/lib/orchestrator/workspaces/types";
import { useWorkspace } from "@/providers/WorkspaceContext";
import { BorderTrail } from "./motion-primitives/core/border-trail";
import { useOrganization } from "@/lib/orchestrator/organizations/api";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip";
import InviteHandler from "./InviteHandler";

// Multi-language Welcome Banner Component
const DextegoBanner = () => {
  const [currentLanguageIndex, setCurrentLanguageIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);

  const welcomeTexts = [
    { text: "Welcome to Dextego", language: "English", code: "en" },
    { text: "Bienvenido a Dextego", language: "Spanish", code: "es" },
    { text: "Bienvenue à Dextego", language: "French", code: "fr" },
    { text: "Willkommen bei Dextego", language: "German", code: "de" },
    { text: "Dextego へようこそ", language: "Japanese", code: "ja" },
    { text: "欢迎来到 Dextego", language: "Chinese", code: "zh" },
    { text: "Bem-vindo ao Dextego", language: "Portuguese", code: "pt" },
    { text: "Добро пожаловать в Dextego", language: "Russian", code: "ru" },
    { text: "Dextego에 오신 것을 환영합니다", language: "Korean", code: "ko" },
    { text: "Benvenuto a Dextego", language: "Italian", code: "it" },
    { text: "Welkom bij Dextego", language: "Dutch", code: "nl" },
    { text: "Velkommen til Dextego", language: "Norwegian", code: "no" },
    { text: "Välkommen till Dextego", language: "Swedish", code: "sv" },
    { text: "Tervetuloa Dextegoon", language: "Finnish", code: "fi" },
    { text: "Dextego'ya hoş geldiniz", language: "Turkish", code: "tr" },
    { text: "مرحبًا بك في Dextego", language: "Arabic", code: "ar" },
    { text: "Dextego में आपका स्वागत है", language: "Hindi", code: "hi" },
    { text: "Selamat datang di Dextego", language: "Indonesian", code: "id" },
    { text: "Vítejte v Dextego", language: "Czech", code: "cs" },
    { text: "Witamy w Dextego", language: "Polish", code: "pl" }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(false);

      // Wait for exit animation to complete before changing the language
      setTimeout(() => {
        setCurrentLanguageIndex((prevIndex) => (prevIndex + 1) % welcomeTexts.length);
        setIsAnimating(true);
      }, 800);
    }, 5000); // Show each language for 5 seconds

    return () => {
      clearInterval(interval);
    };
  }, []);

  const currentLanguage = welcomeTexts[currentLanguageIndex];

  return (
    <div className="h-32 sm:h-28 flex flex-col items-center justify-center relative">
      <div className="text-center mb-1 relative">
        <AnimatePresence mode="wait">
          <motion.div key={`1-${currentLanguageIndex}`}>
            <motion.div
              key={currentLanguageIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 10 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 1 }}
              className="text-center"
            >
              <TextEffect
                per={welcomeTexts[currentLanguageIndex].code === "hi" ? "word" : "char"}
                preset="fade"
                speedReveal={0.4}
                className="py-2 text-4xl sm:text-5xl font-bold bg-linear-to-r from-[#FFDDF9] via-[#C5E6FF] to-[#A3EBAA] inline-block text-transparent bg-clip-text"
              >
                {welcomeTexts[currentLanguageIndex].text}
              </TextEffect>
            </motion.div>

          </motion.div>

          <motion.div
            key={`2-${currentLanguageIndex}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 0 }}
            transition={{ duration: 1 }}
            className="mt-3 text-sm font-medium text-gray-300"
          >
            <span className="px-3 py-1 bg-gray-800/50 rounded-full">
              {currentLanguage.language}
            </span>
          </motion.div>

        </AnimatePresence>
      </div>

    </div>
  );
};


const InfoButton = () => {
  const [showInfo, setShowInfo] = useState(false);

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <AnimatePresence>
        {showInfo && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-16 right-0 bg-gray-800/90 backdrop-blur-md rounded-lg p-4 mb-4 w-64 shadow-lg"
          >
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-semibold text-blue-300">Workspace Types</h3>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowInfo(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={16} />
              </motion.button>
            </div>

            <ul className="text-sm text-gray-300 space-y-2">
              <li className="flex items-start">
                <span className="text-blue-400 mr-2">•</span>
                <span><strong>Personal</strong> - Private workspace for individual use</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-400 mr-2">•</span>
                <span><strong>Organization</strong> - Shared workspace for team collaboration</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-400 mr-2">•</span>
                <span>Workspaces with a 🔒 icon require a PIN</span>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`${showInfo ? 'bg-blue-700' : 'bg-blue-600/70 hover:bg-blue-500/80'
          } backdrop-blur-sm rounded-full p-3 shadow-lg flex items-center transition-colors`}
        onClick={() => setShowInfo(!showInfo)}
      >
        <Info className="w-5 h-5" />
        <span className="ml-2 mr-1 text-sm font-medium">
          {showInfo ? 'Close' : 'Info'}
        </span>
      </motion.button>
    </div>
  );
};



const WorkspaceSelection: React.FC = () => {
  const {
    workspaces,
    getWorkspaces,
    currentWorkspace,
    setCurrentWorkspace,
    verifyPin,
    pinVerificationStatus,
    error,
    showWorkspaceSelector,
    setShowWorkspaceSelector,
    createWorkspace,
    isLoading
  } = useWorkspace();

  const { createOrganization, isLoading: isOrganizationLoading } = useOrganization();


  const pathname = usePathname();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<WorkspaceDetailSerializer | null>(null);
  const [pins, setPins] = useState(["", "", "", ""]);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const pinInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    logo: null,
    logoPreview: "",
    workspace_type: "organization",
    contact_email: "",
    website: "",
    slug: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const router = useRouter();
  const fileInputRef = useRef(null);
  const [showWorkspaceInfo, setShowWorkspaceInfo] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const searchParams = useSearchParams();
  const inviteToken = searchParams.get("invite");

  // Reset state when selector is closed
  useEffect(() => {
    if (!showWorkspaceSelector) {
      setSelectedWorkspace(null);
      setShowPinDialog(false);
      setPins(["", "", "", ""]);
      setLocalError(null);
      setShowCreateModal(false);
    }
  }, [showWorkspaceSelector]);

  // Handle PIN input
  const handlePinChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    setLocalError(null);

    const newPins = [...pins];
    newPins[index] = value;
    setPins(newPins);

    if (value && index < 3) {
      pinInputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (!pins[index] && index > 0) {
        const newPins = [...pins];
        newPins[index - 1] = "";
        setPins(newPins);
        pinInputRefs.current[index - 1]?.focus();
      }
    }
  };

  // Close with escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (showPinDialog) {
          setShowPinDialog(false);
        } else if (pathname !== "/app" || currentWorkspace) {
          // Only allow closing if not on the base app path without a workspace
          setShowWorkspaceSelector(false);
        }
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [setShowWorkspaceSelector, showPinDialog, pathname, currentWorkspace]);

  if (!showWorkspaceSelector) return null;

  const handleWorkspaceClick = async (workspace: WorkspaceDetailSerializer) => {
    try {
      if (workspace.security_status?.has_pin) {
        setSelectedWorkspace(workspace);
        setShowPinDialog(true);
        setPins(["", "", "", ""]);
        setTimeout(() => pinInputRefs.current[0]?.focus(), 100);
      } else {
        await setCurrentWorkspace(workspace.id, false);
        router.push(`/app/${workspace.id}`);
      }
    } catch {
      toast.error("Failed to switch workspace. Please try again.");
    }
  };

  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedWorkspace) return;

    const pin = pins.join("");
    try {
      const success = await verifyPin(selectedWorkspace.id, pin);
      if (success) {
        await setCurrentWorkspace(selectedWorkspace.id, false);
        setShowPinDialog(false);
        setPins(["", "", "", ""]);
        router.push(`/app/${selectedWorkspace.id}`);
      }
    } catch (err) {
      toast.error("Failed to verify PIN. Please try again.");
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: (i: number) => ({
      opacity: 1,
      scale: 1,
      transition: {
        delay: i * 0.1,
        duration: 0.3,
        ease: "easeOut"
      }
    }),
    hover: {
      scale: 1.03,
      transition: {
        duration: 0.2,
        ease: "easeInOut"
      }
    },
    tap: {
      scale: 0.97,
      transition: {
        duration: 0.1
      }
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        duration: 0.5,
        bounce: 0.3
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.2
      }
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.length < 3) {
      newErrors.name = "Name must be at least 3 characters";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      newErrors.contact_email = "Invalid email format";
    }

    if (formData.website && !/^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/.test(formData.website)) {
      newErrors.website = "Invalid website URL";
    }

    if (formData.slug) {
      if (!/^[a-z0-9-]+$/.test(formData.slug)) {
        newErrors.slug = "Slug can only contain lowercase letters, numbers, and hyphens";
      } else if (formData.slug.length < 3) {
        newErrors.slug = "Slug must be at least 3 characters";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



  // Modify the handleSubmit function to use organization API
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const { logoPreview, ...data } = formData;

      // Prepare organization data
      const organizationData = {
        name: data.name,
        description: data.description,
        contact_email: data.contact_email || "",
        website: data.website || "",
        slug: data.slug || undefined, // Only include if provided
        logo: data.logo,
        workspace_name: data.name
      };

      // Create organization
      const response = await createOrganization(organizationData);

      if (response && response.data) {
        toast.success(`Organization "${data.name}" created successfully`);

        // Refresh workspaces list to include the new workspace
        await getWorkspaces();

        // Close modal
        setShowCreateModal(false);

        // Reset form
        setFormData({
          name: "",
          description: "",
          logo: null,
          logoPreview: "",
          workspace_type: "organization",
          contact_email: "",
          website: "",
          slug: ""
        });
      }
    } catch (error) {
      console.error("Failed to create organization:", error);

      // Enhanced error handling
      if (error.response?.data?.message) {
        toast.error(`Error: ${error.response.data.message}`);
      } else if (error.message) {
        toast.error(`Error: ${error.message}`);
      } else {
        toast.error("Failed to create organization. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine if close button should be shown
  // Only disable the close button if on base app path with no workspace
  const canCloseSelector = pathname !== "/app" || currentWorkspace !== null;

  // Get workspace type label
  const getWorkspaceTypeLabel = (type: string) => {
    return type === "personal" ? "Personal" : "Organization";
  };

  if (!showWorkspaceSelector) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md"
      style={{
        backgroundImage: 'radial-gradient(circle at center, rgba(30, 41, 59, 0.5) 0%, rgba(15, 23, 42, 0.8) 100%)',
        backgroundSize: 'cover'
      }}
    >
      {inviteToken && <InviteHandler onClose={() => {
        // Clear the invite token from URL
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete("invite");
        window.history.replaceState({}, '', newUrl.toString());
      }} />}
      <div className="min-h-screen text-white p-4 sm:p-6 md:p-8 flex flex-col">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-6xl mx-auto"
        >
          {canCloseSelector && (
            <div className="absolute right-8 top-8">
              <motion.button
                whileHover={{ rotate: 90 }}
                transition={{ duration: 0.2 }}
                onClick={() => setShowWorkspaceSelector(false)}
                className="p-2 hover:bg-gray-800/50 rounded-full transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </motion.button>
            </div>
          )}

          <div className="h-screen w-full flex items-center justify-center">
            <div className="flex-1 w-full items-center justify-center">
              {isLoading ? (
                <div className="flex justify-center items-center h-64">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"
                  />
                </div>
              ) : (
                <div className="w-full max-w-6xl items-center justify-center">
                  {/* Welcome Animation Section */}
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-center mb-8"
                  >
                    <DextegoBanner />

                    <h2 className="text-2xl md:text-3xl font-medium mt-6 mb-2">Select your workspace to continue</h2>
                  </motion.div>

                  <AnimatePresence mode="popLayout">
                    <div className="flex flex-wrap items-center justify-center w-full gap-4">
                      {workspaces && workspaces.length > 0 && workspaces.map((workspace, index) => (
                        <motion.div
                          key={workspace.id}
                          custom={index}
                          variants={cardVariants}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          whileHover="hover"
                          whileTap="tap"
                          layout
                          className={`
                            relative cursor-pointer rounded-xl overflow-hidden w-48
                            group transition-all duration-300
                            ${currentWorkspace?.id === workspace.id ? "ring-1 ring-[##C5E6FF]" : ""}
                          `}
                          onClick={() => handleWorkspaceClick(workspace)}
                          onMouseEnter={() => setIsHovered(true)}
                          onMouseLeave={() => setIsHovered(false)}
                        >

                          <div className="relative aspect-square bg-gray-800/60 backdrop-blur-xs rounded-xl">
                            {workspace.logo_url ? (
                              <Image
                                src={workspace.logo_url as string}
                                alt={workspace.name}
                                width={192}
                                height={192}
                                className="object-cover w-full h-full"
                              />
                            ) : (
                              <div className="flex items-center justify-center w-full h-full bg-linear-to-br from-gray-700/50 to-gray-900/50">
                                <span className="text-2xl sm:text-3xl font-bold">
                                  {workspace.name.charAt(0)}
                                </span>
                              </div>
                            )}

                            <motion.div
                              initial={{ opacity: 1 }}
                              whileHover={{ opacity: 0 }}
                              className="absolute inset-0 bg-linear-to-t from-black/90 via-black/60 to-transparent"
                            >
                              <div className="absolute bottom-0 left-0 right-0 p-3">
                                <h3 className="text-base sm:text-lg font-semibold mb-1 line-clamp-1 text-center">{workspace.name}</h3>
                                {workspace.description ? (
                                  <p className="text-xs sm:text-sm text-gray-300 line-clamp-2">{workspace.description}</p>
                                ) : (
                                  <p className="text-xs sm:text-sm text-blue-300 line-clamp-1 text-center">
                                    {getWorkspaceTypeLabel(workspace.workspace_type)}
                                  </p>
                                )}
                              </div>
                            </motion.div>

                            {/* Add workspace type badge */}
                            <motion.div
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              className="absolute top-2 left-2"
                            >
                              <motion.div
                                whileHover={{ scale: 1.1 }}
                                className="px-2 py-1 bg-black/50 backdrop-blur-xs rounded-md text-xs font-medium"
                              >
                                {getWorkspaceTypeLabel(workspace.workspace_type)}
                              </motion.div>
                            </motion.div>

                            {workspace?.security_status?.has_pin && (
                              <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="absolute top-2 right-2"
                              >
                                <motion.div
                                  whileHover={{ scale: 1.1 }}
                                  className="p-1.5 bg-black/50 backdrop-blur-xs rounded-full"
                                >
                                  <svg
                                    className="w-4 h-4 text-yellow-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                </motion.div>
                              </motion.div>
                            )}

                            {isHovered && <BorderTrail
                              className='absolute bg-linear-to-r from-[#FFDDF9] via-[#C5E6FF] to-[#A3EBAA]'
                              size={150}
                            />}

                          </div>
                        </motion.div>
                      ))}
                      <motion.div
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        whileHover="hover"
                        whileTap="tap"
                        custom={workspaces?.length || 0}
                        onClick={() => setShowCreateModal(true)}
                        className="relative cursor-pointer rounded-xl overflow-hidden w-48 group transition-all duration-300"
                      >
                        <div className="aspect-square bg-linear-to-br from-blue-500/20 to-blue-700/20 backdrop-blur-xs flex flex-col items-center justify-center">
                          <motion.div
                            initial={{ scale: 1 }}
                            whileHover={{ scale: 1.1 }}
                            className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mb-4"
                          >
                            <Plus className="w-8 h-8 text-blue-400" />
                          </motion.div>
                          <h3 className="text-base font-semibold text-blue-400">New Workspace</h3>
                        </div>
                      </motion.div>
                    </div>
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-xs"
              onClick={() => setShowCreateModal(false)}
            />

            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="bg-gray-800/90 backdrop-blur-xs rounded-xl w-full max-w-3xl relative z-10"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-white">Create Workspace</h2>
                  <motion.button
                    whileHover={{ rotate: 90 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => setShowCreateModal(false)}
                    className="p-2 hover:bg-gray-700/50 rounded-full"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </motion.button>
                </div>

                <form onSubmit={handleSubmit}>
                  {/* Workspace Type Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-300 mb-2">Workspace Type</label>
                    <div className="flex gap-4">
                      <div className="relative">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <label className="flex items-center space-x-2 p-3 bg-gray-700/50 rounded-lg cursor-not-allowed opacity-70">
                                <input
                                  type="radio"
                                  name="workspace_type"
                                  value="personal"
                                  disabled
                                  className="h-4 w-4 text-blue-600"
                                />
                                <span className="text-white">Personal</span>
                                <Info className="w-4 h-4 text-gray-400 ml-1" />
                              </label>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="bg-gray-800 text-gray-300">
                              You can only have one personal workspace.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <label className="flex items-center space-x-2 p-3 bg-gray-700/50 rounded-lg flex-1 cursor-pointer">
                              <input
                                type="radio"
                                name="workspace_type"
                                value="organization"
                                checked={formData.workspace_type === "organization"}
                                onChange={(e) => setFormData(prev => ({ ...prev, workspace_type: e.target.value }))}
                                className="h-4 w-4 text-blue-600"
                              />
                              <span className="text-white">Organization</span>
                              <Info className="w-4 h-4 text-gray-400 ml-1" />
                            </label>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="bg-gray-800 text-gray-300">
                            Create a shared workspace for team collaboration.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>

                  {/* Basic Information - Always visible */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-blue-400 mb-3">Basic Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Name */}
                      <div>
                        <div className="flex items-center mb-1">
                          <label className="text-sm font-medium text-gray-300">Name</label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="w-4 h-4 text-gray-400 ml-1" />
                              </TooltipTrigger>
                              <TooltipContent side="top" className="bg-gray-800 text-gray-300">
                                The name of your {formData.workspace_type === "organization" ? "organization" : "workspace"}.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <motion.input
                          whileFocus={{ scale: 1.02 }}
                          type="text"
                          placeholder={formData.workspace_type === "organization" ? "Organization Name" : "Workspace Name"}
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          className={`w-full p-3 bg-gray-700/50 rounded-lg outline-hidden focus:ring-2 focus:ring-blue-500 transition-all text-white ${errors.name ? "ring-2 ring-red-500" : ""}`}
                        />
                        {errors.name && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-red-400 text-sm mt-1"
                          >
                            {errors.name}
                          </motion.p>
                        )}
                      </div>

                      {/* Description */}
                      <div>
                        <div className="flex items-center mb-1">
                          <label className="text-sm font-medium text-gray-300">Description</label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="w-4 h-4 text-gray-400 ml-1" />
                              </TooltipTrigger>
                              <TooltipContent side="top" className="bg-gray-800 text-gray-300">
                                A brief description of your {formData.workspace_type === "organization" ? "organization" : "workspace"}.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <motion.input
                          whileFocus={{ scale: 1.02 }}
                          type="text"
                          placeholder="Description"
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          className={`w-full p-3 bg-gray-700/50 rounded-lg outline-hidden focus:ring-2 focus:ring-blue-500 transition-all text-white ${errors.description ? "ring-2 ring-red-500" : ""}`}
                        />
                        {errors.description && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-red-400 text-sm mt-1"
                          >
                            {errors.description}
                          </motion.p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Organization-specific fields - Only shown for organization type */}
                  {formData.workspace_type === "organization" && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mb-6 overflow-hidden"
                    >
                      <h3 className="text-sm font-medium text-blue-400 mb-3">Organization Details</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Slug */}
                        <div>
                          <div className="flex items-center mb-1">
                            <label className="text-sm font-medium text-gray-300">Slug (Optional)</label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="w-4 h-4 text-gray-400 ml-1" />
                                </TooltipTrigger>
                                <TooltipContent side="top" className="bg-gray-800 text-gray-300">
                                  A unique identifier for your organization, used in URLs.
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <motion.input
                            whileFocus={{ scale: 1.02 }}
                            type="text"
                            placeholder="organization-slug"
                            value={formData.slug}
                            onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value.toLowerCase() }))}
                            className={`w-full p-3 bg-gray-700/50 rounded-lg outline-hidden focus:ring-2 focus:ring-blue-500 transition-all text-white ${errors.slug ? "ring-2 ring-red-500" : ""}`}
                          />
                          {errors.slug && (
                            <motion.p
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="text-red-400 text-sm mt-1"
                            >
                              {errors.slug}
                            </motion.p>
                          )}
                        </div>

                        {/* Contact Email */}
                        <div>
                          <div className="flex items-center mb-1">
                            <label className="text-sm font-medium text-gray-300">Contact Email (Optional)</label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="w-4 h-4 text-gray-400 ml-1" />
                                </TooltipTrigger>
                                <TooltipContent side="top" className="bg-gray-800 text-gray-300">
                                  The primary contact email for your organization.
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <motion.input
                            whileFocus={{ scale: 1.02 }}
                            type="email"
                            placeholder="contact@organization.com"
                            value={formData.contact_email}
                            onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                            className={`w-full p-3 bg-gray-700/50 rounded-lg outline-hidden focus:ring-2 focus:ring-blue-500 transition-all text-white ${errors.contact_email ? "ring-2 ring-red-500" : ""}`}
                          />
                          {errors.contact_email && (
                            <motion.p
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="text-red-400 text-sm mt-1"
                            >
                              {errors.contact_email}
                            </motion.p>
                          )}
                        </div>

                        {/* Website */}
                        <div>
                          <div className="flex items-center mb-1">
                            <label className="text-sm font-medium text-gray-300">Website (Optional)</label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="w-4 h-4 text-gray-400 ml-1" />
                                </TooltipTrigger>
                                <TooltipContent side="top" className="bg-gray-800 text-gray-300">
                                  Your organization's website URL.
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <motion.input
                            whileFocus={{ scale: 1.02 }}
                            type="url"
                            placeholder="https://organization.com"
                            value={formData.website}
                            onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                            className={`w-full p-3 bg-gray-700/50 rounded-lg outline-hidden focus:ring-2 focus:ring-blue-500 transition-all text-white ${errors.website ? "ring-2 ring-red-500" : ""}`}
                          />
                          {errors.website && (
                            <motion.p
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="text-red-400 text-sm mt-1"
                            >
                              {errors.website}
                            </motion.p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Logo Upload */}
                  <div className="mb-6">
                    <div className="flex items-center mb-1">
                      <label className="text-sm font-medium text-gray-300">Logo (Optional)</label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-4 h-4 text-gray-400 ml-1" />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="bg-gray-800 text-gray-300">
                            Upload a logo for your {formData.workspace_type === "organization" ? "organization" : "workspace"}. Recommended size: 256x256px.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          // Check file size (under 5MB)
                          if (file.size > 5 * 1024 * 1024) {
                            setErrors(prev => ({ ...prev, logo: "File size should be under 5MB" }));
                            return;
                          }

                          // Check file type
                          if (!file.type.startsWith("image/")) {
                            setErrors(prev => ({ ...prev, logo: "Only image files are allowed" }));
                            return;
                          }

                          setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.logo;
                            return newErrors;
                          });

                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setFormData(prev => ({
                              ...prev,
                              logo: file,
                              logoPreview: reader.result as string
                            }));
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      accept="image/*"
                      className="hidden"
                    />

                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => fileInputRef.current?.click()}
                      className="cursor-pointer relative w-full h-48 rounded-lg overflow-hidden bg-gray-700/50 flex items-center justify-center"
                    >
                      {formData.logoPreview ? (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="w-full h-full"
                        >
                          <img
                            src={formData.logoPreview}
                            alt="Logo preview"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                            <p className="text-white">Change Logo</p>
                          </div>
                        </motion.div>
                      ) : (
                        <div className="text-center p-6">
                          <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-gray-400">Upload Logo</p>
                          <p className="text-xs text-gray-500 mt-1">Max size: 5MB</p>
                        </div>
                      )}
                    </motion.div>
                    {errors.logo && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-400 text-sm mt-1"
                      >
                        {errors.logo}
                      </motion.p>
                    )}
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex gap-3 pt-4">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="flex-1 px-4 py-3 bg-gray-700/50 rounded-lg hover:bg-gray-600/50 transition-colors text-gray-100"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 px-4 py-3 bg-blue-600 rounded-lg hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {isSubmitting ? (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                        />
                      ) : (
                        <>
                          <Check className="w-5 h-5 mr-2" />
                          {formData.workspace_type === "organization"
                            ? "Create Organization"
                            : "Create Workspace"}
                        </>
                      )}
                    </motion.button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPinDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gray-800/90 backdrop-blur-xs p-6 rounded-xl w-full max-w-sm mx-4"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">Enter PIN</h2>
                <motion.button
                  whileHover={{ rotate: 90 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => setShowPinDialog(false)}
                  className="p-2 hover:bg-gray-700/50 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>
              </div>

              <form onSubmit={handlePinSubmit} className="space-y-6">
                <div className="flex justify-center space-x-3">
                  {pins.map((pin, index) => (
                    <input
                      key={index}
                      ref={el => pinInputRefs.current[index] = el}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      value={pin}
                      onChange={(e) => handlePinChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className="w-12 h-12 text-center text-xl bg-gray-700/50 backdrop-blur-xs
                               rounded-lg focus:ring-2 focus:ring-blue-500 outline-hidden
                               transition-all duration-200 text-gray-100"
                    />
                  ))}
                </div>

                {localError && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-red-400 text-sm text-center"
                  >
                    {localError}
                  </motion.p>
                )}

                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={() => setShowPinDialog(false)}
                    className="flex-1 px-4 py-2.5 bg-gray-700/50 backdrop-blur-xs rounded-lg
                             hover:bg-gray-600/50 transition-colors text-sm text-gray-200"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={pins.some(p => !p) || pinVerificationStatus === "verifying"}
                    className="flex-1 px-4 py-2.5 bg-blue-900/90 backdrop-blur-xs rounded-lg
                             hover:bg-blue-700/90 transition-colors disabled:opacity-50
                             disabled:cursor-not-allowed text-sm text-white"
                  >
                    {pinVerificationStatus === "verifying" ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-5 h-5 border-2 border-white border-t-transparent rounded-full mx-auto"
                      />
                    ) : (
                      "Confirm"
                    )}
                  </motion.button>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  type="button"
                  onClick={() => {/* Implement forgot PIN */ }}
                  className="w-full text-sm text-gray-400 hover:text-white transition-colors text-center"
                >
                  Forgot PIN?
                </motion.button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Empty State */}
      <AnimatePresence>
        {workspaces && workspaces.length === 0 && !isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center z-40"
          >
            <div className="bg-gray-800/80 backdrop-blur-md p-8 rounded-xl max-w-md text-center">
              <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Plus className="w-10 h-10 text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold mb-3">No workspaces found</h2>
              <p className="text-gray-300 mb-6">
                Let's create your first workspace to get started with your projects
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-500 rounded-lg text-white font-medium"
              >
                Create Workspace
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Subtle Info Button */}
      <InfoButton />
    </motion.div>
  );
};

export { WorkspaceSelection };